import type { ReactNode } from "react";

import { I } from "./icons";

export type TagTone =
  | "generated"
  | "template"
  | "fast"
  | "phone"
  | "logged"
  | "est"
  | "planned"
  | "measured"
  | "synced"
  | "console"
  | "yours"
  | "cycle"
  | "untracked"
  | "neutral";

/** What each tone says when no children are given — a tag is always a word. */
const WORD: Record<TagTone, string> = {
  generated: "generated",
  template: "template",
  fast: "fast day",
  phone: "phone",
  console: "console",
  logged: "logged",
  est: "est.",
  planned: "planned",
  measured: "measured",
  synced: "synced",
  yours: "yours",
  cycle: "cycle-affected",
  untracked: "untracked",
  neutral: "",
};

/**
 * Provenance and status after a figure (SNIPPETS §9): phone and console = sky, logged = brand,
 * est. = rest, planned = dashed outline, measured = success, synced = outline with a check,
 * yours = a food the user entered (ADR-032): ground fill, hairline, ink. cycle = cycle-affected
 * (kit §25): a weigh-in, intake figure or energy line that fell in the cycle-tracking window.
 * fast = "fast day" (kit §32): ochre, a fast the person chose. untracked (kit §32): rest, like
 * est. — a day marked as not fully logged; it says "left out", never "missed" (ADR-049).
 * The plan's other tags (kit §32, ADR-043): generated = filled by the planner, dashed because it
 * is not yet real; template = placed from a saved day, the `yours` look.
 *
 * When to use: after any measured or estimated figure. `<Tag tone="phone" />` reads "phone";
 * pass children to say something else in the same tone.
 */
export function Tag({ tone, children }: { tone: TagTone; children?: ReactNode }) {
  return (
    <span className={`k-tag k-tag--${tone}`}>
      {tone === "synced" ? I.tick : null}
      {children ?? WORD[tone]}
    </span>
  );
}
